import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../components/Input";
import Dropdown from "../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import { getStartEndOfMonth } from "../../../../utils/methods";
import { ListFilters } from "../../../../interfaces/invoice.interface";
import useDebounce from "../../../../hooks/useDebounce";

interface FilterProps {
  properties: any[];
  allProperties: any[];
  customers: any[];
  allCustomers: any[];
  invoiceStatus: any[];
  allInvoiceStatus: any[];
  createdBy: any[];
  allCreatedBy: any[];
  allReviewedByOptions: any[];
  reviewedByOptions: any[];
  verificationStatus: any[];
  allVerificationStatus: any[];
  managers: any[];
  allManagers: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  properties, allProperties, customers, allCustomers, invoiceStatus, allInvoiceStatus, createdBy, allCreatedBy,
  reviewedByOptions, allReviewedByOptions, verificationStatus, allVerificationStatus, managers, allManagers, dropdownSearchData, onDropdownSearchChange,
  initialFilters, onApply, onReset
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const defaultDates = useMemo(() => getStartEndOfMonth(), []);

  // Calculate initial filtersApplied state
  const isApplied = initialFilters.searchText !== '' ||
    initialFilters.property !== '' ||
    initialFilters.customer !== '' ||
    initialFilters.invoiceStatus !== '' ||
    initialFilters.createdBy !== '' ||
    initialFilters.reviewedBy !== '' ||
    initialFilters.isVerified !== '' ||
    initialFilters.managedBy !== '' ||
    initialFilters.startDate !== defaultDates.startDate ||
    initialFilters.endDate !== defaultDates.endDate;

  const [filtersApplied, setFiltersApplied] = useState(isApplied);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle Real-time application
  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = filtersToApply.searchText !== '' ||
      filtersToApply.property !== '' ||
      filtersToApply.customer !== '' ||
      filtersToApply.invoiceStatus !== '' ||
      filtersToApply.createdBy !== '' ||
      filtersToApply.reviewedBy !== '' ||
      filtersToApply.isVerified !== '' ||
      filtersToApply.managedBy !== '' ||
      filtersToApply.startDate !== defaultDates.startDate ||
      filtersToApply.endDate !== defaultDates.endDate;

    setFiltersApplied(applied);

    onApply(filtersToApply);
  }, [debouncedSearchText, filters.property, filters.customer, filters.invoiceStatus, filters.createdBy, filters.reviewedBy, filters.isVerified, filters.managedBy, filters.startDate, filters.endDate, defaultDates, onApply]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      searchText: '',
      property: '',
      customer: '',
      invoiceStatus: '',
      createdBy: '',
      reviewedBy: '',
      isVerified: '',
      managedBy: '',
      ...getStartEndOfMonth(),
    };
    setFilters(defaults);
    onReset(); // This likely calls parent to reset state to defaults
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.startDate}
                name={VALIDATION_FIELD_MAPPING.startDate}
                max={filters.endDate}
                placeholder={t('invoices.addList.pageTexts.filters.startDate')}
                value={filters.startDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.endDate}
                name={VALIDATION_FIELD_MAPPING.endDate}
                min={filters.startDate || undefined}
                placeholder={t('invoices.addList.pageTexts.filters.endDate')}
                value={filters.endDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.property ? [filters.property] : []}
                options={(properties || []).map((property: any) => ({
                  label: property.name,
                  value: property.id
                }))}
                allOptions={(allProperties || []).map((property: any) => ({
                  label: property.name,
                  value: property.id
                }))}
                id={VALIDATION_FIELD_MAPPING.property}
                name={VALIDATION_FIELD_MAPPING.property}
                searchText={dropdownSearchData.property}
                onSearch={onDropdownSearchChange}
                placeholder={t("invoices.addList.pageTexts.filters.selectProperty")}
                label={t("invoices.addList.pageTexts.filters.property")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.customer ? [filters.customer] : []}
                options={(customers || []).map((customer: any) => ({
                  label: customer.name,
                  value: customer.id
                }))}
                allOptions={(allCustomers || []).map((customer: any) => ({
                  label: customer.name,
                  value: customer.id
                }))}
                id={VALIDATION_FIELD_MAPPING.customer}
                name={VALIDATION_FIELD_MAPPING.customer}
                searchText={dropdownSearchData.customer}
                onSearch={onDropdownSearchChange}
                placeholder={t("invoices.addList.pageTexts.filters.selectCustomer")}
                label={t("invoices.addList.pageTexts.filters.customer")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.invoiceStatus ? [filters.invoiceStatus] : []}
                options={(invoiceStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allInvoiceStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.invoiceStatus}
                name={VALIDATION_FIELD_MAPPING.invoiceStatus}
                searchText={dropdownSearchData.invoiceStatus}
                onSearch={onDropdownSearchChange}
                placeholder={t("invoices.addList.pageTexts.filters.selectInvoiceStatus")}
                label={t("invoices.addList.pageTexts.filters.invoiceStatus")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.createdBy ? [filters.createdBy] : []}
                options={(createdBy || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allCreatedBy || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.createdBy}
                name={VALIDATION_FIELD_MAPPING.createdBy}
                searchText={dropdownSearchData.createdBy}
                onSearch={onDropdownSearchChange}
                placeholder={t("invoices.addList.pageTexts.filters.selectCreatedBy")}
                label={t("invoices.addList.pageTexts.filters.createdBy")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.reviewedBy ? [filters.reviewedBy] : []}
                options={(reviewedByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allReviewedByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.reviewedBy}
                name={VALIDATION_FIELD_MAPPING.reviewedBy}
                searchText={dropdownSearchData.reviewedBy}
                placeholder={t('invoices.addList.pageTexts.filters.selectReviewedBy')}
                label={t('invoices.addList.pageTexts.filters.reviewedBy')}
                onChange={handleInputChange}
                onSearch={onDropdownSearchChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.managedBy ? [filters.managedBy] : []}
                options={(managers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                allOptions={(allManagers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                id={VALIDATION_FIELD_MAPPING.managedBy}
                name={VALIDATION_FIELD_MAPPING.managedBy}
                searchText={dropdownSearchData.managedBy}
                onSearch={onDropdownSearchChange}
                placeholder={t("invoices.addList.pageTexts.filters.selectManagedBy")}
                label={t("invoices.addList.pageTexts.filters.managedBy")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.isVerified ? [filters.isVerified] : []}
                options={(verificationStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allVerificationStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.isVerified}
                name={VALIDATION_FIELD_MAPPING.isVerified}
                searchText={dropdownSearchData.isVerified}
                placeholder={t('invoices.addList.pageTexts.filters.selectVerificationStatus')}
                label={t('invoices.addList.pageTexts.filters.verificationStatus')}
                onChange={handleInputChange}
                onSearch={onDropdownSearchChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("invoices.addList.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;